import type { WorkshopOpeningHours } from '@/types';

/**
 * Workshop opening hours as default clock times (#4094, #4095).
 *
 * Floating ("during the day") work is stored at midnight, which is a day
 * marker and not a clock time. Defaults derived from it — a pickup estimate,
 * a courtesy-car loan start, the exact time after switching type — start
 * from the day's opening time instead. When the workshop has not saved its
 * hours, or the day is closed, the long-standing 08–17 default applies.
 */
export const FALLBACK_OPEN_TIME = '08:00';
export const FALLBACK_CLOSE_TIME = '17:00';

const WEEKDAY_KEYS: (keyof WorkshopOpeningHours)[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

const HHMM = /^(\d{1,2}):(\d{2})$/;

/** Minutes since midnight for `HH:mm`, or null when the value is not a clock time. */
export function hhmmToMinutes(value: string): number | null {
  const match = HHMM.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

function minutesToHhmm(total: number): string {
  const hours = String(Math.floor(total / 60)).padStart(2, '0');
  const minutes = String(total % 60).padStart(2, '0');
  return `${hours}:${minutes}`;
}

function parseCivilDate(value: string | Date): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/**
 * The workshop's open/close for a civil day (`yyyy-MM-dd…` string or local Date).
 * Null when the hours are unknown, the day is closed or the times are malformed.
 */
export function openingHoursForDate(
  date: string | Date,
  hours: WorkshopOpeningHours | null | undefined,
): { open: string; close: string; openMinutes: number; closeMinutes: number } | null {
  if (!hours) return null;
  const day = parseCivilDate(date);
  if (!day) return null;
  const key = WEEKDAY_KEYS[day.getDay()];
  const entry = key ? hours[key] : null;
  if (!entry) return null;
  const open = hhmmToMinutes(entry.open);
  const close = hhmmToMinutes(entry.close);
  if (open === null || close === null || close <= open) return null;
  return {
    open: minutesToHhmm(open),
    close: minutesToHhmm(close),
    openMinutes: open,
    closeMinutes: close,
  };
}

/** Opening time (`HH:mm`) for a civil day; 08:00 when unknown or closed. */
export function openTimeForDate(
  date: string | Date,
  hours: WorkshopOpeningHours | null | undefined,
): string {
  return openingHoursForDate(date, hours)?.open ?? FALLBACK_OPEN_TIME;
}
